import { SYSTEM_FONTS } from 'common/constants';
import type { GoogleFontItem, SCFontItem } from 'types/fonts';
import type { IOrganizationFonts } from 'types/organization';

export const FONT_CATEGORIES = ['serif', 'sans-serif', 'display', 'handwriting', 'monospace'] as const;

export type FontCategory = (typeof FONT_CATEGORIES)[number];
export type FontSource = 'system' | 'story' | 'organization' | 'google';

export type ResolvedFont = {
	family: string;
	fontFamily: string;
	source: FontSource;
	category?: FontCategory;
	variants?: string[];
	faces?: SCFontItem[];
	google?: GoogleFontItem;
};

export type AvailableFontsQuery = {
	nameRegex?: RegExp;
	category?: FontCategory;
	skip?: number;
};

export type FontResolver = {
	resolveExact: (fontFamily: string) => ResolvedFont | undefined;
	search: (query: AvailableFontsQuery) => { fonts: ResolvedFont[]; hasMore: boolean; total: number };
	diagnostics: Array<{ family: string; winner: FontSource; alsoFoundIn: FontSource[] }>;
};

const PAGE_SIZE = 5;

const SYSTEM_FONT_CATEGORIES: Record<keyof typeof SYSTEM_FONTS, FontCategory> = {
	VARELA_ROUND: 'sans-serif',
	MIRIAM_LIBRE: 'sans-serif',
	SUEZ_ONE: 'serif',
	SECULAR_ONE: 'sans-serif',
	ALEF: 'sans-serif',
	RUBIK: 'sans-serif',
};

const BUILT_IN_FONTS: ResolvedFont[] = [
	{ family: 'Assistant', fontFamily: 'Assistant, sans-serif', source: 'system', category: 'sans-serif' },
	...Object.entries(SYSTEM_FONTS).map(([key, font]) => ({
		family: primaryFontFamily(font.fontFamily)!,
		fontFamily: font.fontFamily,
		source: 'system' as const,
		category: SYSTEM_FONT_CATEGORIES[key as keyof typeof SYSTEM_FONTS],
	})),
];

export function createFontResolver({
	storyFonts,
	organizationFonts,
	googleFonts,
}: {
	storyFonts?: IOrganizationFonts;
	organizationFonts?: IOrganizationFonts;
	googleFonts: GoogleFontItem[];
}): FontResolver {
	const googleByFamily = new Map(googleFonts.map(font => [canonicalFamilyName(font.family), font]));
	const winners = new Map<string, ResolvedFont>();
	const sources = new Map<string, FontSource[]>();

	const add = (font: ResolvedFont): void => {
		const key = canonicalFamilyName(font.family);
		if (!key) return;
		const foundIn = sources.get(key) ?? [];
		if (!foundIn.includes(font.source)) foundIn.push(font.source);
		sources.set(key, foundIn);
		if (!winners.has(key)) winners.set(key, font);
	};

	BUILT_IN_FONTS.forEach(add);
	groupStoredFonts(storyFonts, 'story', googleByFamily).forEach(add);
	groupStoredFonts(organizationFonts, 'organization', googleByFamily).forEach(add);
	googleFonts.forEach(font =>
		add({
			family: font.family,
			fontFamily: toCssFontFamilyValue(font.family),
			source: 'google',
			category: isFontCategory(font.category) ? font.category : undefined,
			variants: [...font.variants],
			google: font,
		})
	);

	const ordered = [...winners.values()];
	return {
		resolveExact: value => winners.get(canonicalFontFamily(value)),
		search: ({ nameRegex, category, skip = 0 }) => {
			const matcher = nameRegex ? new RegExp(nameRegex.source, nameRegex.flags) : undefined;
			const matches = ordered.filter(font => {
				if (category !== undefined && font.category !== category) return false;
				if (!matcher) return true;
				matcher.lastIndex = 0;
				return matcher.test(normalizeFamilyName(font.family));
			});
			const fonts = matches.slice(skip, skip + PAGE_SIZE);
			return { fonts, total: matches.length, hasMore: skip + fonts.length < matches.length };
		},
		diagnostics: [...sources.entries()].flatMap(([family, foundIn]) =>
			foundIn.length > 1 ? [{ family, winner: winners.get(family)!.source, alsoFoundIn: foundIn.slice(1) }] : []
		),
	};
}

export function canonicalFontFamily(value: string): string {
	return canonicalFamilyName(primaryFontFamily(value) ?? '');
}

export function canonicalFamilyName(value: string): string {
	return normalizeFamilyName(value).toLowerCase();
}

export function primaryFontFamily(value: string): string | null {
	let quote: '"' | "'" | null = null;
	let escaped = false;
	let end = value.length;
	for (let index = 0; index < value.length; index += 1) {
		const character = value[index];
		if (escaped) {
			escaped = false;
			continue;
		}
		if (character === '\\') {
			escaped = true;
			continue;
		}
		if (quote) {
			if (character === quote) quote = null;
			continue;
		}
		if (character === '"' || character === "'") {
			quote = character;
			continue;
		}
		if (character === ',') {
			end = index;
			break;
		}
	}

	const primary = value.slice(0, end).trim();
	if (!primary) return null;
	const unquoted = primary
		.replace(/^(?:"([\s\S]*)"|'([\s\S]*)')$/, '$1$2')
		.replace(/\\([\\"'])/g, '$1')
		.trim();
	return unquoted || null;
}

export function isFontCategory(value: unknown): value is FontCategory {
	return typeof value === 'string' && FONT_CATEGORIES.includes(value as FontCategory);
}

function groupStoredFonts(
	fonts: IOrganizationFonts | undefined,
	source: 'story' | 'organization',
	googleByFamily: Map<string, GoogleFontItem>
): ResolvedFont[] {
	const groups = new Map<string, SCFontItem[]>();
	for (const font of Object.values(fonts ?? {})) {
		const key = canonicalFamilyName(font.fontFamily);
		if (!key) continue;
		const group = groups.get(key) ?? [];
		group.push(font);
		groups.set(key, group);
	}

	return [...groups.entries()].map(([key, faces]) => {
		const first = faces[0]!;
		const google = first.fontType === 'google' ? googleByFamily.get(key) : undefined;
		const variants = [...new Set(faces.flatMap(face => face.variants ?? []))];
		return {
			family: first.fontFamily.trim(),
			fontFamily: toCssFontFamilyValue(first.fontFamily),
			source,
			category: google && isFontCategory(google.category) ? google.category : undefined,
			...(variants.length ? { variants } : {}),
			faces,
		};
	});
}

function normalizeFamilyName(value: string): string {
	return value.trim().replace(/\s+/g, ' ');
}

function toCssFontFamilyValue(value: string): string {
	const family = value.trim();
	return `"${family.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`;
}
